/**
 * Pure helpers of the guided-flow option: which chrome variant a workspace
 * renders, which choices are already made, and what the foot of a step
 * offers. Shared by both chrome variants.
 */
import type {
  FmOperationalChoiceSummaryItem,
  FmOperationalChoiceSummaryLead,
  FmOperationalCompactGuidanceDescriptor,
  FmOperationalGuidanceChrome,
  FmOperationalGuidanceDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';

/** The chrome variant a workspace renders; `null` when it is not a guided flow. */
export function operationalGuidanceChrome(
  guidance: FmOperationalGuidanceDescriptor | undefined,
): FmOperationalGuidanceChrome | null {
  if (!guidance) return null;
  return guidance.chrome === 'compact' ? 'compact' : 'standard';
}

/** The compact guidance, when the workspace declares it. */
export function compactGuidance(
  descriptor: FmOperationalWorkspaceDescriptor,
): FmOperationalCompactGuidanceDescriptor | null {
  return descriptor.guidance?.chrome === 'compact' ? descriptor.guidance : null;
}

/**
 * The choices MADE before the active step, and the compact lead figure once
 * its step is behind. A step's own choice is its work below, so repeating it
 * above would open the first step on a summary of what it is about to ask.
 */
export function guidedChoicesMade(
  guidance: FmOperationalGuidanceDescriptor,
  stages: readonly FmOperationalStageDescriptor[],
  activeStageId: string,
): {
  items: readonly FmOperationalChoiceSummaryItem[];
  lead: FmOperationalChoiceSummaryLead | null;
} {
  const index = stages.findIndex((stage) => stage.id === activeStageId);
  const madeBefore = new Set(stages.slice(0, index).map((stage) => stage.id));
  const items = guidance.summary?.items.filter((item) => madeBefore.has(item.stageId)) ?? [];
  const candidate = guidance.chrome === 'compact' ? guidance.summary?.lead : undefined;
  const lead = candidate && madeBefore.has(candidate.stageId) ? candidate : null;
  return { items, lead };
}

/** What the foot of a guided step offers: the way back, the way on, and the sentence between. */
export function guidedTransitionMoves(
  guidance: FmOperationalGuidanceDescriptor,
  stages: readonly FmOperationalStageDescriptor[],
  activeStage: FmOperationalStageDescriptor,
) {
  const index = stages.findIndex((stage) => stage.id === activeStage.id);
  const previous = stages[index - 1];
  const next = stages[index + 1];
  // A step that cannot be left yet still shows its move forward, disabled and
  // with the next step's reason in place of the transition sentence, so the
  // owner sees what is missing instead of a step with no way on.
  const blockedReason = next?.status === 'blocked' ? next.disabledReason : undefined;
  const forward = activeStage.transitionActionLabel && next ? next : null;
  // Settled choices have no way back: the step before would only re-open them.
  const back =
    previous && previous.status !== 'blocked' && !guidance.summary?.readOnly ? previous : null;
  // A step held back with no move forward has nothing for the next step's
  // reason or the hand-over sentence to explain; alone at the foot of the step
  // they read as an orphan line. The step's own notice says what is missing.
  const heldBack = !forward && next?.status === 'blocked';
  const sentence = heldBack ? undefined : (blockedReason ?? activeStage.transition);
  return { back, forward, blockedReason, sentence };
}
